import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gameTime } from "./clock";
import { ShotChart, ScoreProgression } from "./CenterCharts";
import { Comparison, Leaders, TeamLineup } from "./GameCenterParts";
import { BoxScore } from "./BoxScore";
import { GamePlan } from "./GamePlan";
import { groupRows, poolHit } from "@/components/arena/arenaData";
import { MARKET_LABEL, pct, evText, lineText, isFresh } from "@/components/value/valueFormat";
import { MarketCheckCard } from "./MarketCheckCard";
import "./GameCenter.css";

const logo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);
const LINEUP_TAG = { expected: "Expected", confirmed: "Confirmed", historical: "Final starters", unavailable: "Unavailable" };

function Tabs({ items, value, onChange, testPrefix }) {
  return (
    <div className="tv-tabs">
      {items.map(([k, label]) => <button key={k} type="button" className={value === k ? "is-active" : ""} onClick={() => onChange(k)} data-testid={`${testPrefix}-${k}`}>{label}</button>)}
    </div>
  );
}

const FLOW_COL = { away: "#ff6b6b", home: "#4fb0ff" };

// Cumulative score-flow chart: each team's running points across Q1→Q4, actual (solid) vs pre-tip prediction (dashed).
function ScoreFlow({ game, q, byCode, showPred = true }) {
  const series = ["away", "home"].map((s) => {
    const a = q[s] || [], t = byCode[game[`${s}_code`]];
    let k = 0;
    for (let i = 0; i < 4; i++) if (a[i] != null) k = i + 1;
    const actCum = [0];
    for (let i = 0; i < k; i++) actCum.push(actCum[i] + (a[i] || 0));
    const predCum = [0];
    for (let i = 0; i < 4; i++) predCum.push(predCum[i] + (t?.by_q[i] || 0));
    return { s, code: game[`${s}_code`], actCum, predCum };
  });
  const maxY = Math.max(10, ...series.flatMap((d) => [...d.actCum, ...d.predCum]));
  const top = Math.ceil(maxY / 10) * 10;
  const xFor = (i) => (i / 4) * 100;
  const yFor = (v) => 96 - (v / top) * 92;
  const poly = (arr) => arr.map((v, i) => `${xFor(i)},${yFor(v)}`).join(" ");
  return (
    <div className="tv-flow" data-testid="tv-flow">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="tv-flow-svg" aria-label="Score flow">
        {[0, 1, 2, 3, 4].map((i) => <line key={i} x1={xFor(i)} y1="2" x2={xFor(i)} y2="98" className="tv-flow-grid" vectorEffect="non-scaling-stroke" />)}
        {series.map((d) => (
          <g key={d.s}>
            {showPred && <polyline points={poly(d.predCum)} fill="none" stroke={FLOW_COL[d.s]} strokeDasharray="3 3" strokeWidth="1.5" opacity="0.65" vectorEffect="non-scaling-stroke" />}
            {d.actCum.length > 1 && <polyline points={poly(d.actCum)} fill="none" stroke={FLOW_COL[d.s]} strokeWidth="2.25" vectorEffect="non-scaling-stroke" />}
          </g>
        ))}
      </svg>
      <div className="tv-flow-x"><span>Q1</span><span>Q2</span><span>Q3</span><span>Q4</span></div>
      <div className="tv-flow-legend" data-testid="tv-flow-legend">
        {series.map((d) => <span key={d.s} style={{ color: FLOW_COL[d.s] }}>{d.code}</span>)}
        <em>— πραγματικό{showPred ? " · --- πρόβλεψη" : ""}</em>
      </div>
    </div>
  );
}

function Quarters({ game, center }) {
  const started = game.status === "in" || game.status === "post";
  const [showPred, setShowPred] = useState(() => localStorage.getItem("nbacube_show_pred") === "1");
  const setPred = (v) => { setShowPred(v); localStorage.setItem("nbacube_show_pred", v ? "1" : "0"); };
  const proj = useGameData(`/score-projection/${game.id}`, game.id, game.status);
  const byCode = proj?.teams ? Object.fromEntries(proj.teams.map((t) => [t.code, t])) : null;

  // Pre-game: show the model's forecast per period (Q1 · Q2 · half · Q3 · Q4 · total).
  if (!started && byCode) {
    return (
      <>
      <table className="tv-quarters tv-quarters-proj" data-testid="tv-quarters-proj">
        <thead><tr><th>Μοντέλο</th><th>Q1</th><th>Q2</th><th className="tv-half">Ημ.</th><th>Q3</th><th>Q4</th><th>Σύν.</th></tr></thead>
        <tbody>{["away", "home"].map((s) => {
          const t = byCode[game[`${s}_code`]];
          return (
            <tr key={s} data-testid={`tv-proj-row-${s}`}>
              <th><img src={logo(game[`${s}_code`])} alt="" />{game[`${s}_code`]}</th>
              {t ? (<>
                <td>{t.by_q[0]}</td><td>{t.by_q[1]}</td>
                <td className="tv-half">{t.by_half[0]}</td>
                <td>{t.by_q[2]}</td><td>{t.by_q[3]}</td>
                <td className="tv-total">{t.proj_total}</td>
              </>) : <td colSpan={6}>–</td>}
            </tr>
          );
        })}</tbody>
      </table>
      </>
    );
  }

  const q = center?.quarters || {};

  // Live / finished with a frozen pre-tip projection: show actual on top and the prediction beneath,
  // each completed period coloured by how close the prediction landed; remaining periods show a LIVE
  // re-estimate (pace-adjusted from what has happened so far) that refreshes as each period ends.
  if (started && byCode) {
    const devClass = (act, pred, good, ok) => {
      if (act == null || pred == null) return "";
      const d = Math.abs(act - pred);
      return d <= good ? "tvc-good" : d <= ok ? "tvc-ok" : "tvc-off";
    };
    const sideLive = (s) => {
      const a = q[s] || [], t = byCode[game[`${s}_code`]];
      if (!t) return null;
      let k = 0;
      for (let i = 0; i < 4; i++) if (a[i] != null) k = i + 1;  // completed quarters
      const elapsedAct = a.slice(0, k).reduce((x, y) => x + (y || 0), 0);
      const elapsedPred = t.by_q.slice(0, k).reduce((x, y) => x + y, 0) || 1;
      const pace = k ? Math.max(0.7, Math.min(1.4, elapsedAct / elapsedPred)) : 1;
      const liveQ = t.by_q.map((v, i) => (i < k ? a[i] : Math.round(v * pace)));
      return { k, liveQ, liveTotal: liveQ.reduce((x, y) => x + (y || 0), 0) };
    };
    const inProg = game.status === "in";
    const Cell = ({ act, pretip, live, completed, total, good, ok }) => (
      <td className={total ? "tv-total" : ""}>
        <span className="tvc-act">{act != null ? act : "·"}</span>
        {showPred && (completed
          ? <span className={`tvc-pred ${devClass(act, pretip, good, ok)}`}>{pretip}</span>
          : <span className="tvc-pred tvc-live">{live != null ? live : "–"}</span>)}
      </td>
    );
    return (
      <>
        <div className="tv-cmp-toggle" data-testid="tv-cmp-toggle">
          <button type="button" className={showPred ? "is-active" : ""} onClick={() => setPred(true)} data-testid="tv-toggle-both">Πραγματικό + Πρόβλεψη</button>
          <button type="button" className={!showPred ? "is-active" : ""} onClick={() => setPred(false)} data-testid="tv-toggle-real">Μόνο πραγματικά</button>
        </div>
        <table className="tv-quarters tv-quarters-cmp" data-testid="tv-quarters-cmp">
          <thead><tr><th>Ομάδα</th><th>Q1</th><th>Q2</th><th className="tv-half">Ημ.</th><th>Q3</th><th>Q4</th><th>Σύν.</th></tr></thead>
          <tbody>{["away", "home"].map((s) => {
            const a = q[s] || [], t = byCode[game[`${s}_code`]], lv = sideLive(s);
            const hA = a[0] != null && a[1] != null ? a[0] + a[1] : null;
            const hLive = lv ? lv.liveQ[0] + lv.liveQ[1] : null;
            const finalTotal = game[`${s}_pts`];
            return (
              <tr key={s} data-testid={`tv-cmp-row-${s}`}>
                <th><img src={logo(game[`${s}_code`])} alt="" />{game[`${s}_code`]}</th>
                <Cell act={a[0]} pretip={t?.by_q[0]} live={lv?.liveQ[0]} completed={a[0] != null} good={2} ok={5} />
                <Cell act={a[1]} pretip={t?.by_q[1]} live={lv?.liveQ[1]} completed={a[1] != null} good={2} ok={5} />
                <Cell act={hA} pretip={t?.by_half[0]} live={hLive} completed={hA != null} good={3} ok={7} />
                <Cell act={a[2]} pretip={t?.by_q[2]} live={lv?.liveQ[2]} completed={a[2] != null} good={2} ok={5} />
                <Cell act={a[3]} pretip={t?.by_q[3]} live={lv?.liveQ[3]} completed={a[3] != null} good={2} ok={5} />
                <Cell act={finalTotal} pretip={t?.proj_total} live={lv?.liveTotal}
                      completed={!inProg && finalTotal != null} total good={5} ok={12} />
              </tr>
            );
          })}</tbody>
        </table>
        {showPred && (
          <p className="tv-cmp-legend" data-testid="tv-cmp-legend">
            <span className="tvc-act">Πραγματικό</span> · πρόβλεψη: <span className="tvc-good">κοντά</span> <span className="tvc-ok">μέτρια</span> <span className="tvc-off">μακριά</span>
            {inProg && <> · <span className="tvc-live">ζωντανή επανεκτίμηση</span></>}
          </p>
        )}
        <ScoreFlow game={game} q={q} byCode={byCode} showPred={showPred} />
      </>
    );
  }

  const n = Math.max(4, q.away?.length || 0, q.home?.length || 0);
  const head = Array.from({ length: n }, (_, i) => (i < 4 ? `Q${i + 1}` : `OT${i > 4 ? i - 3 : ""}`));
  return (
    <table className="tv-quarters" data-testid="tv-quarters">
      <thead><tr><th>Team</th>{head.map((h) => <th key={h}>{h}</th>)}<th>Total</th></tr></thead>
      <tbody>{["away", "home"].map((s) => (
        <tr key={s}><th><img src={logo(game[`${s}_code`])} alt="" />{game[`${s}_code`]}</th>
          {head.map((h, i) => <td key={h}>{q[s]?.[i] ?? "–"}</td>)}<td className="tv-total">{game[`${s}_pts`] ?? "–"}</td></tr>
      ))}</tbody>
    </table>
  );
}

export const Section = ({ title, children, right, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!collapsible)
    return <div className="gc-section"><div className="gc-section-head"><h3>{title}</h3>{right}</div>{children}</div>;
  return (
    <div className="gc-section">
      <div className="gc-section-head gc-section-toggle" role="button" tabIndex={0} data-testid="gc-section-toggle"
        onClick={() => setOpen((o) => !o)}>
        <h3><span className={`gc-chevron ${open ? "is-open" : ""}`}>▸</span>{title}</h3>
        {open && right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
      </div>
      {open && children}
    </div>
  );
};

const MARKET_ORDER = ["pts", "reb", "ast", "fg3m", "pra"];
const TEAM_MARKET = { spread: "SPREAD", total: "TOTAL", moneyline: "MONEYLINE" };

// Player value picks for the selected game, grouped per market; each row opens the player's page.
function PlayerValuePicks({ picks, onOpenPlayer }) {
  if (!picks?.length) return null;
  const markets = [...MARKET_ORDER, ...[...new Set(picks.map((p) => p.market))].filter((m) => !MARKET_ORDER.includes(m))]
    .map((m) => [m, picks.filter((p) => p.market === m).sort((a, b) => b.ev - a.ev)]).filter(([, l]) => l.length);
  return (
    <Section title="Value picks · players" right={<span className="gc-val-count" data-testid="gc-value-count">{picks.length}</span>}>
      <div className="gc-value" data-testid="gc-value-picks">
        {markets.map(([m, list]) => (
          <div key={m} className="gc-val-market" data-testid={`gc-value-market-${m}`}>
            <div className="gc-val-mhead"><b>{MARKET_LABEL[m] || m.toUpperCase()}</b><span>{list.length}</span></div>
            {list.map((p) => (
              <button key={p.id} type="button" className={`gc-val-row ${p.result || ""}`} onClick={() => onOpenPlayer?.(p.player_id)} data-testid={`gc-value-pick-${p.id}`}>
                <span className="gc-val-player">{p.player_name}</span>
                <span className="gc-val-line">{p.label || lineText(p)}</span>
                <span className="gc-val-odds">{Number(p.odds).toFixed(2)}</span>
                <span className="gc-val-chance">{pct(p.p)}</span>
                <span className={`gc-val-ev ${p.ev >= 0 ? "pos" : "neg"}`}>{evText(p.ev)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
}

// Team value markets (Winner / Handicap / Over-Under) for the selected game; opens the team-projections value view.
function TeamValuePicks({ picks, onOpen }) {
  if (!picks?.length) return null;
  return (
    <Section title="Value picks · team markets" right={<span className="gc-val-count" data-testid="gc-team-value-count">{picks.length}</span>}>
      <div className="gc-value" data-testid="gc-team-value-picks">
        {picks.map((p, i) => (
          <button key={`${p.market}-${i}`} type="button" className={`gc-val-row gc-team-row ${p.result || ""}`} onClick={() => onOpen?.(p.game_id)} data-testid={`gc-team-value-${p.market}`}>
            <span className="gc-val-mk">{TEAM_MARKET[p.market] || p.market}</span>
            <span className="gc-val-line">{p.label}{isFresh(p.moved_at) && <em className="gc-moved" data-testid={`gc-team-value-moved-${p.market}`} title="Η γραμμή ή το edge άλλαξε πρόσφατα">Μόλις ενημερώθηκε</em>}</span>
            <span className="gc-val-odds">{p.price ? Number(p.price).toFixed(2) : "—"}</span>
            <span className="gc-val-chance">{p.edge_label}</span>
          </button>
        ))}
      </div>
    </Section>
  );
}

// Loads one game-scoped resource and reloads every 30s while the game is live.
export function useGameData(path, id, status) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setData(null);
    const load = () => api.get(path).then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const t = status === "in" ? setInterval(load, 30000) : null;
    return () => { alive = false; if (t) clearInterval(t); };
  }, [path, id, status]);
  return data;
}

// Admin-only log of starting-five (5άδα) changes for this game with edge movement.
const fmtEv = (v) => (v == null ? "—" : evText(v));
function LineupChanges({ gameId, status }) {
  const data = useGameData(`/games/${gameId}/lineup-changes`, status === "pre" ? gameId : null, status);
  const changes = data?.changes || [];
  if (!changes.length) return null;
  return (
    <Section title="Αλλαγές 5άδας" right={<span className="gc-val-count" data-testid="gc-lineup-changes-count">{changes.length}</span>}>
      <div className="gc-lchanges" data-testid="gc-lineup-changes">
        {changes.map((c) => (
          <div key={c.id} className="gc-lchange" data-testid={`gc-lineup-change-${c.id}`}>
            <div className="gc-lc-head">
              {c.team_logo && <img src={c.team_logo} alt="" />}
              <b>{c.team_code}</b>
              <span className={`gc-lc-src is-${c.source}`}>{c.source === "admin" ? "Admin" : "ESPN"}</span>
              <time>{new Date(c.at).toLocaleString()}</time>
            </div>
            <div className="gc-lc-inout">
              {c.in.map((p) => <span key={`i${p.player_id}`} className="gc-lc-in">▲ {p.name}</span>)}
              {c.out.map((p) => <span key={`o${p.player_id}`} className="gc-lc-out">▼ {p.name}</span>)}
            </div>
            {c.edges?.length > 0 && (
              <ul className="gc-lc-edges">
                {c.edges.slice(0, 6).map((e, i) => (
                  <li key={i}>
                    <b>{e.player_name}</b>
                    <span>{lineText({ market: e.market, side: e.side, line: e.line })}</span>
                    <em>{fmtEv(e.old_ev)} → {fmtEv(e.new_ev)}</em>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

export function GameCenter({ game, rows = [], playerPicks = [], teamPicks = [], onOpenPlayer, onOpenTeamGame, check, onCloseCheck, onSelectCheck }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [detail, setDetail] = useState("team");
  const [period, setPeriod] = useState("season");
  const [refreshing, setRefreshing] = useState(false);
  const id = game?.id, status = game?.status;
  const started = status === "in" || status === "post";
  const refreshBox = async () => {
    if (!isAdmin || refreshing) return;
    setRefreshing(true);
    try {
      const r = await api.post(`/games/${id}/refresh-box`);
      toast.success(r.data.box_status === "complete" ? `Στατιστικά ανανεώθηκαν · ${r.data.rows} παίκτες` : `Μερική ενημέρωση · ${r.data.rows} παίκτες (${r.data.box_status})`);
    } catch (e) { toast.error(errorText(e)); }
    finally { setRefreshing(false); }
  };
  const center = useGameData(`/gamecenter/${id}`, id, status);
  const lineups = useGameData(`/games/${id}/lineups`, started ? null : id, "pre");
  const comparison = useGameData(`/gamecenter/${id}/comparison`, started ? null : id, null);
  if (!game) return <section className="tv-panel"><p className="tv-empty">Select a game.</p></section>;
  const t = gameTime(game);
  const teams = lineups?.teams || [];
  // Confirmation hit rate (starters % - bench %) across the main markets, from this game's roster rows.
  const HIT_METRICS = ["pts", "reb", "ast", "fg3m", "pra"];
  const sideHit = (code) => {
    const g = groupRows(rows.filter((r) => r.game_id === id && r.team === code));
    const s = poolHit(g.starters, HIT_METRICS);
    const b = poolHit(g.bench, HIT_METRICS);
    if (s == null && b == null) return undefined;
    return `${s ?? "—"}% - ${b ?? "—"}%`;
  };
  const myPlayerPicks = playerPicks.filter((p) => p.game_id === id);
  const myTeamPicks = teamPicks.filter((p) => p.game_id === id);
  const checkRow = check ? rows.find((r) => r.player_id === check.playerId && r.game_id === id) : null;
  const checkPick = check ? myPlayerPicks
    .filter((p) => p.player_id === check.playerId && p.market === check.stat)
    .sort((a, b) => Math.abs((a.odds ?? 99) - 1.8) - Math.abs((b.odds ?? 99) - 1.8))[0] || null : null;
  return (
    <section className="tv-panel tv-center" aria-label="Game center" data-testid="tv-center-panel">
      <div className="tv-scroll">
        {checkRow && <MarketCheckCard row={checkRow} stat={check.stat} pick={checkPick} rows={rows} onClose={onCloseCheck} onSelectPlayer={onSelectCheck} />}
        <div className="tv-scoreboard gc-board">
          <div className="tv-score-top"><span>{status === "in" ? "In progress" : status === "pre" ? "Pre-game" : ""}</span>
            <span className={`tv-clock ${t.critical ? "is-critical" : ""}`}>{t.label}</span><span className="tv-venue">{center?.venue || ""}</span>
            {started && (
              <button type="button" className="gc-refresh" onClick={refreshBox} disabled={!isAdmin || refreshing}
                title={isAdmin ? "Άμεση ανανέωση στατιστικών (box score)" : "Ανανέωση στατιστικών — μόνο για admin"} data-testid="gc-refresh-box">
                {refreshing ? <Loader2 size={12} className="gc-refresh-spin" /> : <RefreshCw size={12} />}<span>Ανανέωση στατιστικών</span>
              </button>
            )}
          </div>
          <div className="tv-score-line">
            <span className="tv-big-team"><span className="tv-big-logo" data-testid="gc-hit-away"><img src={logo(game.away_code)} alt="" title={sideHit(game.away_code)} />{game.away_b2b && <em className="tv-b2b" title="Back-to-back: played the night before" data-testid="gc-b2b-away">B2B</em>}</span><b>{game.away_code}</b><small>{game.away_wins != null ? `${game.away_wins}-${game.away_losses}` : ""} · AWAY</small></span>
            <strong data-testid="tv-big-score">{started ? `${game.away_pts ?? 0} – ${game.home_pts ?? 0}` : "VS"}</strong>
            <span className="tv-big-team"><span className="tv-big-logo" data-testid="gc-hit-home"><img src={logo(game.home_code)} alt="" title={sideHit(game.home_code)} />{game.home_b2b && <em className="tv-b2b" title="Back-to-back: played the night before" data-testid="gc-b2b-home">B2B</em>}</span><b>{game.home_code}</b><small>{game.home_wins != null ? `${game.home_wins}-${game.home_losses}` : ""} · HOME</small></span>
          </div>
          <Quarters game={game} center={center} />
        </div>
        <TeamValuePicks picks={myTeamPicks} onOpen={onOpenTeamGame} />
        <PlayerValuePicks picks={myPlayerPicks} onOpenPlayer={onOpenPlayer} />
        {!started && <GamePlan game={game} isAdmin={isAdmin} />}
        {started && <Leaders leaders={center?.leaders} live={status === "in"} game={{ ...game, away_logo: logo(game.away_code), home_logo: logo(game.home_code) }} />}
        {!started && (
          <Section title="Team comparison" right={<Tabs items={[["season", "Season"], ["last5", "Last 5"]]} value={period} onChange={setPeriod} testPrefix="gc-period" />}>
            <Comparison data={comparison} period={period} game={{ ...game, away_logo: logo(game.away_code), home_logo: logo(game.home_code) }} />
          </Section>
        )}
        {!started && (
          <Section title="Starting lineups" right={lineups && <span className={`gc-tag is-${lineups.status}`} data-testid="gc-lineup-status">{LINEUP_TAG[lineups.status] || lineups.status}</span>}>
            {!lineups ? <p className="tv-empty">Loading lineups…</p> : (
              <div className="gc-lineups">{teams.map((tm) => <TeamLineup key={tm.side} team={tm} gameId={id} side={tm.side} />)}</div>
            )}
          </Section>
        )}
        {!started && isAdmin && <LineupChanges gameId={id} status={status} />}
        <Section title={status === "in" ? "Live game analytics" : "Game analytics"}>
          <Tabs items={[["team", "Team stats"], ["box", "Box score"], ["shots", "Shot chart"], ["progress", "Score progression"]]} value={detail} onChange={setDetail} testPrefix="tv-detail" />
          {detail === "box" && (started ? <BoxScore box={center?.box} game={game} /> : <p className="tv-empty">Box score appears after tip-off.</p>)}
          {detail === "team" && (center?.team_stats?.length ? (
            <div className="tv-teamstats" data-testid="tv-teamstats">{center.team_stats.map((r) => <div key={r.label}><b>{r.away}</b><span>{r.label}</span><b>{r.home}</b></div>)}</div>
          ) : <p className="tv-empty">{started ? "Team stats not available yet." : "Team stats appear after tip-off."}</p>)}
          {detail === "shots" && (started ? <ShotChart shots={center?.shots} game={game} /> : <p className="tv-empty">Shot chart appears after tip-off.</p>)}
          {detail === "progress" && (started ? <ScoreProgression points={center?.progression} game={game} /> : <p className="tv-empty">Score progression appears after tip-off.</p>)}
        </Section>
      </div>
    </section>
  );
}
